import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  CSSProperties,
  ReactNode,
} from 'react';
import {
  Dialog,
  Modal as ReactAriaModal,
  ModalOverlay as ReactAriaModalOverlay,
} from 'react-aria-components';
import { ErrorBoundary } from 'react-error-boundary';
import { useHotkeysContext } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { AnimatedLoading } from '@actual-app/components/icons/AnimatedLoading';
import { SvgLogo } from '@actual-app/components/icons/logo';
import { SvgDelete } from '@actual-app/components/icons/v0';
import { Input } from '@actual-app/components/input';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { TextOneLine } from '@actual-app/components/text-one-line';
import { theme } from '@actual-app/components/theme';
import { tokens } from '@actual-app/components/tokens';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';
import { AutoTextSize } from 'auto-text-size';

import { FeatureErrorFallback } from '#components/FeatureErrorFallback';
import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { CapaVentanaContext } from '#components/mobile/ui/Hoja';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { color, movimiento, radio, sombra } from '#components/mobile/ui/tokens';
import { useModalState } from '#hooks/useModalState';

import {
  debePintarseEnHoja,
  ModalEnHojaContext,
  useDentroDeHoja,
} from './modalesEnHoja';
import { AsaHoja, BotonCerrarHoja, CabeceraHoja } from './ModalHoja';
import type { TonoHoja } from './ModalHoja';

export const MODAL_Z_INDEX = 3000;

type ModalProps = ComponentPropsWithRef<typeof ReactAriaModal> & {
  name: string;
  isLoading?: boolean;
  noAnimation?: boolean;
  style?: CSSProperties;
  onClose?: () => void;
  wrapperProps?: {
    style?: CSSProperties;
  };
  containerProps?: {
    style?: CSSProperties;
  };
};

export const Modal = ({
  name,
  isLoading = false,
  noAnimation = false,
  style,
  children,
  onClose,
  wrapperProps,
  containerProps,
  ...props
}: ModalProps) => {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const { enableScope, disableScope } = useHotkeysContext();

  // This deactivates any key handlers in the "app" scope
  useEffect(() => {
    enableScope(name);
    return () => disableScope(name);
  }, [enableScope, disableScope, name]);

  const { isHidden, isActive, onClose: closeModal } = useModalState();

  // H01: los modales registrados en `modalesEnHoja.ts` salen en el móvil
  // como Hoja de Rumbo; el resto (y todo en escritorio) sigue igual.
  const enHoja = debePintarseEnHoja(name, isNarrowWidth);

  const handleOnClose = () => {
    closeModal();
    onClose?.();
  };
  // «Atrás» del móvil cierra la ventana de arriba en vez de cambiar de
  // pantalla.
  useCerrarConAtras(isNarrowWidth, handleOnClose);

  return (
    <ErrorBoundary FallbackComponent={FeatureErrorFallback}>
      <ReactAriaModalOverlay
        data-testid={`${name}-modal`}
        isDismissable
        defaultOpen
        onOpenChange={isOpen => !isOpen && handleOnClose?.()}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: MODAL_Z_INDEX,
          fontSize: 14,
          // on mobile, we disable the blurred background for performance reasons
          ...(isNarrowWidth
            ? {
                backgroundColor: enHoja ? color.veloHoja : 'rgba(0, 0, 0, 0.4)',
              }
            : {
                backdropFilter: 'blur(1px) brightness(0.9)',
              }),
          ...style,
        }}
        {...props}
      >
        {/* A container for positioning the modal relative to the visual viewport */}
        <View
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'var(--visual-viewport-height)',
            overflowY: 'auto',
            ...wrapperProps?.style,
            // La hoja se pega abajo, a todo el ancho.
            ...(enHoja && {
              alignItems: 'stretch',
              justifyContent: 'flex-end',
              overflowY: 'hidden',
            }),
          }}
        >
          <ReactAriaModal>
            {modalProps => (
              <Dialog
                aria-label={t('Modal dialog')}
                className={css(styles.lightScrollbar)}
                style={{
                  outline: 'none', // remove focus outline
                  ...(enHoja && { width: '100%' }),
                }}
              >
                <ModalContentContainer
                  noAnimation={noAnimation}
                  isActive={isActive(name)}
                  enHoja={enHoja}
                  {...containerProps}
                  style={
                    enHoja
                      ? estiloHoja(isHidden)
                      : {
                          flex: 1,
                          padding: 10,
                          willChange: 'opacity, transform',
                          maxWidth: '90vw',
                          minWidth: '90vw',
                          maxHeight:
                            'calc(var(--visual-viewport-height) * 0.9)',
                          minHeight: 0,
                          borderRadius: isNarrowWidth ? 24 : 6,
                          //border: '1px solid ' + theme.modalBorder,
                          color: theme.pageText,
                          backgroundColor: theme.modalBackground,
                          opacity: isHidden ? 0 : 1,
                          [`@media (min-width: ${tokens.breakpoint_small})`]: {
                            minWidth: tokens.breakpoint_small,
                          },
                          overflowY: 'auto',
                          ...styles.shadowLarge,
                          ...containerProps?.style,
                        }
                  }
                >
                  {enHoja && <AsaHoja onCerrar={handleOnClose} />}
                  <View style={{ paddingTop: 0, flex: 1, flexShrink: 0 }}>
                    <ErrorBoundary FallbackComponent={FeatureErrorFallback}>
                      <ModalEnHojaContext.Provider value={enHoja}>
                        <CapaVentanaContext.Provider value={MODAL_Z_INDEX}>
                          {typeof children === 'function'
                            ? children(modalProps)
                            : children}
                        </CapaVentanaContext.Provider>
                      </ModalEnHojaContext.Provider>
                    </ErrorBoundary>
                  </View>
                  {isLoading && (
                    <View
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: enHoja
                          ? color.surfaceHoja
                          : theme.pageBackground,
                        borderRadius: enHoja
                          ? `${radio.heroe}px ${radio.heroe}px 0 0`
                          : undefined,
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                      }}
                    >
                      <AnimatedLoading
                        style={{ width: 20, height: 20 }}
                        color={theme.pageText}
                      />
                    </View>
                  )}
                </ModalContentContainer>
              </Dialog>
            )}
          </ReactAriaModal>
        </View>
      </ReactAriaModalOverlay>
    </ErrorBoundary>
  );
};

/**
 * Contenedor de la Hoja de Rumbo (como `mobile/ui/Hoja`): a todo el ancho,
 * esquinas de arriba redondas, hasta el 90 % de alto con scroll. Los
 * `containerProps` del modal (anchos y altos de ventana) no se aplican.
 */
function estiloHoja(isHidden: boolean): CSSProperties {
  return {
    flex: 1,
    width: '100%',
    maxHeight: 'calc(var(--visual-viewport-height) * 0.9)',
    minHeight: 0,
    padding: '8px 16px',
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
    borderRadius: `${radio.heroe}px ${radio.heroe}px 0 0`,
    color: color.fg,
    // B6 · H14: en oscuro, superficie elevada y filo arriba.
    backgroundColor: color.surfaceHoja,
    borderTop: color.bordeHoja,
    boxShadow: sombra.hoja,
    fontVariantNumeric: 'tabular-nums',
    opacity: isHidden ? 0 : 1,
    overflowY: 'auto',
    willChange: 'opacity, transform',
  };
}

type ModalContentContainerProps = {
  style?: CSSProperties;
  noAnimation?: boolean;
  isActive?: boolean;
  enHoja?: boolean;
  children: ReactNode;
};

const ModalContentContainer = ({
  style,
  noAnimation,
  isActive,
  enHoja = false,
  children,
}: ModalContentContainerProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);
  const rotateFactor = useRef(Math.random() * 10 - 5);

  useLayoutEffect(() => {
    if (!contentRef.current) {
      return;
    }

    function setProps() {
      if (!contentRef.current) {
        return;
      }

      if (isActive) {
        contentRef.current.style.transform = 'none';
        contentRef.current.style.willChange = 'auto';
        contentRef.current.style.pointerEvents = 'auto';
      } else if (enHoja) {
        // Hoja tapada por otro modal (p. ej. el selector de cuenta): se
        // queda quieta en su sitio, sin el giro de las ventanas.
        contentRef.current.style.transform = 'none';
        contentRef.current.style.pointerEvents = 'none';
      } else {
        contentRef.current.style.transform = `translateY(-40px) scale(.95) rotate(${rotateFactor.current}deg)`;
        contentRef.current.style.pointerEvents = 'none';
      }
    }

    if (!mounted.current) {
      if (noAnimation) {
        contentRef.current.style.opacity = '1';
        contentRef.current.style.transform = 'translateY(0px) scale(1)';

        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.style.transition =
              'opacity .1s, transform .1s cubic-bezier(.42, 0, .58, 1)';
          }
        }, 0);
      } else {
        // Presupuesto (concepto A): las hojas entran desde abajo con un
        // muelle suave de 320 ms; sin animación con «reducir movimiento».
        const reducir = window.matchMedia?.(
          '(prefers-reduced-motion: reduce)',
        ).matches;
        contentRef.current.style.opacity = '0';
        // La hoja sube desde el borde de abajo; la ventana, 24 px.
        contentRef.current.style.transform = enHoja
          ? 'translateY(100%)'
          : 'translateY(24px) scale(1)';

        setTimeout(() => {
          if (contentRef.current) {
            mounted.current = true;
            contentRef.current.style.transition = reducir
              ? 'none'
              : enHoja
                ? `opacity ${movimiento.fondo}ms, transform ${movimiento.hoja}ms ${movimiento.suave}`
                : 'opacity .2s, transform .32s cubic-bezier(.2, .9, .3, 1.15)';
            contentRef.current.style.opacity = '1';
            setProps();
          }
        }, 0);
      }
    } else {
      setProps();
    }
  }, [noAnimation, isActive, enHoja]);

  return (
    <View
      innerRef={contentRef}
      style={{
        ...style,
        ...(noAnimation && !isActive && { display: 'none' }),
      }}
    >
      {children}
    </View>
  );
};

type ModalButtonsProps = {
  style?: CSSProperties;
  leftContent?: ReactNode;
  focusButton?: boolean;
  children: ReactNode;
};

export const ModalButtons = ({
  style,
  leftContent,
  focusButton = false,
  children,
}: ModalButtonsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusButton && containerRef.current) {
      const button = containerRef.current.querySelector<HTMLButtonElement>(
        'button:not([data-hidden])',
      );

      if (button) {
        button.focus();
      }
    }
  }, [focusButton]);

  return (
    <View
      innerRef={containerRef}
      style={{
        flexDirection: 'row',
        marginTop: 30,
        ...style,
      }}
    >
      {leftContent}
      <View style={{ flex: 1 }} />
      {children}
    </View>
  );
};

type ModalHeaderProps = {
  leftContent?: ReactNode;
  showLogo?: boolean;
  title?: ReactNode;
  rightContent?: ReactNode;
  /** Solo en la Hoja de Rumbo (H01): cajita de icono a la izquierda. */
  icono?: NombreIcono;
  /** Solo en la hoja: rojo si borra o cierra, verde si no. */
  tono?: TonoHoja;
  /** Solo en la hoja: línea pequeña bajo el título. */
  subtitulo?: ReactNode;
};

export function ModalHeader({
  leftContent,
  showLogo,
  title,
  rightContent,
  icono,
  tono,
  subtitulo,
}: ModalHeaderProps) {
  const { t } = useTranslation();
  const enHoja = useDentroDeHoja();
  if (enHoja) {
    return (
      <CabeceraHoja
        title={title}
        subtitulo={subtitulo}
        icono={icono}
        tono={tono}
        leftContent={leftContent}
        rightContent={rightContent}
      />
    );
  }
  return (
    <h1
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        height: 60,
        flex: 'none',
        display: 'flex',
        margin: 0,
        padding: 0,
      }}
    >
      <View
        style={{
          position: 'absolute',
          left: 0,
        }}
      >
        {leftContent}
      </View>

      {(title || showLogo) && (
        <View
          style={{
            textAlign: 'center',
            // We need to force a width for the text-overflow
            // ellipses to work because we are aligning center.
            width: 'calc(100% - 60px)',
          }}
        >
          {showLogo && (
            <SvgLogo
              aria-label={t('Modal logo')}
              width={30}
              height={30}
              style={{ justifyContent: 'center', alignSelf: 'center' }}
            />
          )}
          {title &&
            (typeof title === 'string' || typeof title === 'number' ? (
              <ModalTitle title={`${title}`} />
            ) : (
              title
            ))}
        </View>
      )}

      {rightContent && (
        <View
          style={{
            position: 'absolute',
            right: 0,
          }}
        >
          {rightContent}
        </View>
      )}
    </h1>
  );
}

type ModalTitleProps = {
  title: string;
  isEditable?: boolean;
  getStyle?: (isEditing: boolean) => CSSProperties;
  onEdit?: (isEditing: boolean) => void;
  onTitleUpdate?: (newName: string) => void;
  shrinkOnOverflow?: boolean;
};

export function ModalTitle({
  title,
  isEditable,
  getStyle,
  onTitleUpdate,
  shrinkOnOverflow = false,
}: ModalTitleProps) {
  const [isEditing, setIsEditing] = useState(false);

  const onTitleClick = () => {
    if (isEditable) {
      setIsEditing(true);
    }
  };

  const _onTitleUpdate = (newTitle: string) => {
    if (newTitle !== title) {
      onTitleUpdate?.(newTitle);
    }
    setIsEditing(false);
  };

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing) {
      if (inputRef.current) {
        inputRef.current.scrollLeft = 0;
      }
    }
  }, [isEditing]);

  const style = getStyle?.(isEditing);

  return isEditing ? (
    <Input
      ref={inputRef}
      style={{
        fontSize: 25,
        fontWeight: 700,
        textAlign: 'center',
        ...style,
      }}
      defaultValue={title}
      onUpdate={_onTitleUpdate}
      onEnter={(value, e) => {
        e.preventDefault();
        _onTitleUpdate?.(value);
      }}
    />
  ) : (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {shrinkOnOverflow ? (
        <AutoTextSize
          as={Text}
          minFontSizePx={15}
          maxFontSizePx={25}
          onClick={onTitleClick}
          style={{
            fontSize: 25,
            fontWeight: 700,
            textAlign: 'center',
            ...(isEditable && styles.underlinedText),
            ...style,
          }}
        >
          {title}
        </AutoTextSize>
      ) : (
        <TextOneLine
          onClick={onTitleClick}
          style={{
            fontSize: 25,
            fontWeight: 700,
            textAlign: 'center',
            ...(isEditable && styles.underlinedText),
            ...style,
          }}
        >
          {title}
        </TextOneLine>
      )}
    </View>
  );
}

type ModalCloseButtonProps = {
  onPress: ComponentPropsWithoutRef<typeof Button>['onPress'];
  style?: CSSProperties;
};

export function ModalCloseButton({ onPress, style }: ModalCloseButtonProps) {
  const { t } = useTranslation();
  const enHoja = useDentroDeHoja();
  if (enHoja) {
    return <BotonCerrarHoja onPress={onPress} />;
  }
  return (
    <Button
      variant="bare"
      onPress={onPress}
      style={{ padding: '10px 10px' }}
      aria-label={t('Close')}
    >
      <SvgDelete width={10} style={style} />
    </Button>
  );
}
